import { useEffect, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import type { FieldConfig } from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/main.tsx';
import { getCascadingSelectFieldValue } from './prefill-custom-field-map/get-field-value/get-cascading-select-field-value/index.tsx';
import {
	prefillCustomFieldMap,
	getWorkLogFieldValues,
	workLogSubFieldTypes,
} from './prefill-custom-field-map/index.tsx';
import type { CascadingSelectFieldArrays } from './types.tsx';

export const usePrefillCustomFields = (modalFieldConfig: FieldConfig) => {
	const [{ prefilledCustomFields }] = useTriggerIssueCreateModal();
	const [data, setData] = useState(modalFieldConfig);

	useEffect(() => {
		if (
			!prefilledCustomFields ||
			Object.keys(prefilledCustomFields).length === 0 ||
			isEmpty(modalFieldConfig)
		) {
			setData(modalFieldConfig);
			return;
		}

		const cascadingSelectFieldParentOptions: CascadingSelectFieldArrays = {};
		const cascadingSelectFieldChildOptions: CascadingSelectFieldArrays = {};
		const nextData = { ...modalFieldConfig };
		Object.keys(prefilledCustomFields).forEach((key) => {
			if (key in modalFieldConfig) {
				const prefilledDefaultValue = prefillCustomFieldMap({
					gqlType: modalFieldConfig[key].gqlType,
					defaultValue: prefilledCustomFields[key],
					allowedValues: modalFieldConfig[key]?.allowedValues,
				});

				if (!(prefilledDefaultValue === null || prefilledDefaultValue === undefined))
					nextData[key] = {
						...nextData[key],
						defaultValue: prefilledDefaultValue,
					};

				if (modalFieldConfig[key].gqlType === 'JiraCascadingSelectField') {
					cascadingSelectFieldParentOptions[key] = prefilledCustomFields[key];
				}
			} else if (key.slice(-2) === ':1') {
				const fieldId = key.slice(0, -2);
				cascadingSelectFieldChildOptions[fieldId] = prefilledCustomFields[key];
			} else if (nextData?.worklog && workLogSubFieldTypes.includes(key)) {
				const objectReturned = getWorkLogFieldValues(key, prefilledCustomFields[key]);
				if (objectReturned === Object(objectReturned)) {
					if (nextData.worklog?.defaultValue) {
						nextData.worklog.defaultValue = { ...nextData.worklog.defaultValue, ...objectReturned };
					} else {
						nextData.worklog.defaultValue = objectReturned;
					}
				}
			}
		});
		Object.keys(cascadingSelectFieldParentOptions).forEach((key) => {
			const parentOptionId = cascadingSelectFieldParentOptions[key];
			const childOptionId = cascadingSelectFieldChildOptions[key];
			const prefilledDefaultValue = getCascadingSelectFieldValue({
				parentOptionId,
				childOptionId,
				allowedValues: modalFieldConfig[key]?.allowedValues,
			});
			nextData[key] = {
				...nextData[key],
				defaultValue: prefilledDefaultValue || modalFieldConfig[key].defaultValue,
			};
		});

		setData(nextData);
	}, [modalFieldConfig, prefilledCustomFields]);

	return data;
};
